// Floating Label Input Component with accessibility

import { InputHTMLAttributes, forwardRef, useId } from 'react';
import styles from './Input.module.css';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  suffix?: React.ReactNode;
  showValid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, suffix, showValid, required, className, value, id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const hasValue = value !== undefined && value !== '';
    
    return (
      <div className={`${styles.inputWrapper} ${className || ''}`}>
        <div className={`${styles.inputContainer} ${error ? styles.hasError : ''} ${hasValue ? styles.hasValue : ''}`}>
          <input
            ref={ref}
            id={inputId}
            className={styles.input}
            placeholder={label}
            value={value}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            aria-required={required}
            {...props}
          />
          <label 
            htmlFor={inputId}
            className={`${styles.label} ${required ? styles.required : ''}`}
          >
            {label}
            {required && <span className="sr-only"> (påkrevd)</span>}
          </label>
          {(suffix || showValid) && (
            <div className={styles.suffix} aria-hidden="true">
              {showValid && !error && hasValue && (
                <CheckIcon className={styles.validIcon} />
              )}
              {suffix}
            </div>
          )}
        </div>
        {error && (
          <div id={errorId} className={styles.error} role="alert" aria-live="polite">
            <ErrorIcon aria-hidden="true" />
            {error}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Icons
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="5.5" stroke="currentColor"/>
      <path d="M6 3.5V6.5M6 8.5V8.5" stroke="currentColor" strokeLinecap="round"/>
    </svg>
  );
}
